import { sixDigitHexColor } from "lib/Colors/colorUtils";
import hasMatchingColor from "lib/hasMatchingColor";
import { cloneDeep, isEmpty } from "lib/lodash";
import { immutableUpdate } from "lib/immutableUpdate";
import { createDOMElementFromHTMLString } from "lib/tests/DOMNodeUtils/testSetupFunctions";
import { updateSmartTextPlaceholder } from "lib/textUtils";
import {
  getNodesInRange,
  getAllColoredContent,
  removeColorFromSelection
} from "lib/DOMNodeUtils";
// import TextBoxElement from "state/ui/editor/elements/TextBoxElement";
import { hexToRgb } from "node-vibrant/lib/util";
import { EDITOR_ELEMENTS_MAP } from "lib/constants";
import { isGradientValue } from "lib/gradients/gradientUtils";
// import GridElement from "state/ui/editor/elements/GridElement";

/**
 * @desc returns a string representing the updated textbox value. Includes any rich text updates with colour attributes removed
 * @param {object} element - textbox element which requires DOM manipulation to remove rich text color changes
 */
const removeColorFromRichText = element => {
  const textboxElement = createDOMElementFromHTMLString(element.value);

  textboxElement.style["position"] = "absolute";
  textboxElement.contentEditable = "true";

  const selection = window.getSelection();
  const range = document.createRange();
  range.selectNodeContents(textboxElement);

  selection.removeAllRanges();
  selection.addRange(range);

  const nodesInRange = getNodesInRange(textboxElement);

  if (!nodesInRange) return element.value;

  const coloredNodes = getAllColoredContent(nodesInRange.allNodes);

  let updatedInnerHTML;
  // only strip color styling if coloredNodes are present
  if (coloredNodes.length) {
    removeColorFromSelection({
      textboxElement,
      styledNodes: coloredNodes,
      nodesInRange
    });
    // reassign the new stripped innerHTML value to the element preview
    updatedInnerHTML = textboxElement.innerHTML;
  }

  textboxElement.remove();

  return updatedInnerHTML;
};

const applyColorToImageInstruction = ({
  element,
  color,
  stateElementPreviews,
  selectedGridCellId
}) => {
  const targetInstructionIndex = element.imageInstructions.findIndex(
    instruction => instruction.domId === selectedGridCellId
  );

  let updatedImageInstructions = [...element.imageInstructions];

  // check if element has any updated previews in state
  // which have yet to be saved to image instructions
  const updatedElementPreviews = cloneDeep(stateElementPreviews);
  if (updatedElementPreviews[element.uniqueId]) {
    updatedImageInstructions =
      updatedElementPreviews[element.uniqueId].imageInstructions;
  }

  updatedImageInstructions[targetInstructionIndex] = {
    ...updatedImageInstructions[targetInstructionIndex],
    color
  };

  return updatedImageInstructions;
};

/**
 * @desc returns an object containing preview updates for individual element attribute colour updates
 * @param {boolean} copyOriginal - boolean for trigger to spread element attribute preview properties into new preview
 * @param {object} el - object containing individual element attributes requiring color update
 * @param {string} originalColor - string representing the HEX value for orignal elements attribute colour
 * @param {string} color - string representing the HEX value for colour change
 */
const transformColor = ({ el, copyOriginal = false, originalColor, color }) => {
  let preview = {};

  // include keys from fillColor, shadow and outline but dump anything else
  if (copyOriginal) {
    preview = { ...el };
  }

  // When originalColor is a gradient, replace entire matching gradient fills
  // with the new color (solid or different gradient) instead of updating stops.
  if (isGradientValue(originalColor)) {
    const originalJson = JSON.stringify(originalColor);

    if (
      el.color &&
      isGradientValue(el.color) &&
      JSON.stringify(el.color) === originalJson
    ) {
      preview.color = color;
    }

    if (
      el.backgroundColor &&
      isGradientValue(el.backgroundColor) &&
      JSON.stringify(el.backgroundColor) === originalJson
    ) {
      preview.backgroundColor = color;
    }

    if (el.fillColors) {
      preview.fillColors = el.fillColors.map(fc => {
        const fcPreview = copyOriginal ? { ...fc } : {};
        if (
          fc.color &&
          isGradientValue(fc.color) &&
          JSON.stringify(fc.color) === originalJson
        ) {
          fcPreview.color = color;
        }
        return copyOriginal ? fcPreview : { ...fc, ...fcPreview };
      });
    }

    return preview;
  }

  if (el.type === EDITOR_ELEMENTS_MAP.TEXTBOX || el.type === "table2Cell") {
    const parser = new DOMParser();
    const doc = parser.parseFromString(el.value, "text/html");
    const spans = doc.getElementsByTagName("span");

    // If el.value has span tags within then it is rich text
    if (spans.length) {
      for (let i = 0; i < spans.length; i++) {
        let span = spans[i];
        let style = span.getAttribute("style") || "";

        let currentRgbValues = style.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
        const originalColorRgb = hexToRgb(originalColor);
        const newRgbValues = hexToRgb(color);
        if (currentRgbValues !== null) {
          // Converts currentRgbValues to array of ints
          currentRgbValues = [
            parseInt(currentRgbValues[1]),
            parseInt(currentRgbValues[2]),
            parseInt(currentRgbValues[3])
          ];

          // Only adjust styling when the orginal update all color matches the current spans color
          if (
            JSON.stringify(originalColorRgb) ===
            JSON.stringify(currentRgbValues)
          ) {
            style = style.replace(
              /rgb\(\d+,\s*\d+,\s*\d+\)/g,
              "rgb(" + newRgbValues.join(", ") + ")"
            );

            // Replace old RGB values with new values
            span.setAttribute("style", style);
          }
        }
      }
      const newHtmlCode = doc.body.innerHTML;
      preview.value = newHtmlCode;
      preview.displayValue = newHtmlCode;
    }
  }

  if (el.type === EDITOR_ELEMENTS_MAP.GRID) {
    preview.imageInstructions = el.imageInstructions.map(cell => {
      if (cell.color && sixDigitHexColor(cell.color) === originalColor) {
        return {
          ...cell,
          color
        };
      } else {
        return cell;
      }
    });
  }

  if (el.color && isGradientValue(el.color)) {
    // Only update stops within an existing gradient when the replacement
    // color is a hex string. A gradient stop cannot hold a gradient object.
    if (!isGradientValue(color)) {
      const gradient = el.color;
      const hasMatch = gradient.stops.some(
        stop => sixDigitHexColor(stop.color) === originalColor
      );
      if (hasMatch) {
        preview.color = {
          ...gradient,
          stops: gradient.stops.map(stop =>
            sixDigitHexColor(stop.color) === originalColor
              ? { ...stop, color }
              : stop
          )
        };
      }
    }
  } else if (el.color && sixDigitHexColor(el.color) === originalColor) {
    preview.color = color;
  }

  if (el.backgroundColor && isGradientValue(el.backgroundColor)) {
    if (!isGradientValue(color)) {
      const gradient = el.backgroundColor;
      const hasMatch = gradient.stops.some(
        stop => sixDigitHexColor(stop.color) === originalColor
      );
      if (hasMatch) {
        preview.backgroundColor = {
          ...gradient,
          stops: gradient.stops.map(stop =>
            sixDigitHexColor(stop.color) === originalColor
              ? { ...stop, color }
              : stop
          )
        };
      }
    }
  } else if (
    el.backgroundColor &&
    sixDigitHexColor(el.backgroundColor) === originalColor
  ) {
    preview.backgroundColor = color;
  }

  if (el.fillColors) {
    preview.fillColors = el.fillColors.map(args =>
      transformColor({ el: args, copyOriginal: true, originalColor, color })
    );
  }

  if (el.shadow) {
    preview.shadow = transformColor({
      el: el.shadow,
      copyOriginal: true,
      originalColor,
      color
    });
  }

  if (
    el.outline &&
    el.outline.color &&
    sixDigitHexColor(el.outline.color) === originalColor
  ) {
    preview.outline = { ...el.outline, ...preview.outline, color };
  }

  return preview;
};

/**
 * @desc returns an object with element uniqueId as keys, containing attribute changes for matching colour
 * @param {object} designData - designData object
 * @param {string} originalColor - string representing the HEX value for original elements attribute colour
 * @param {string} color - string representing the HEX value for colour change
 */
const updateAllMatchingElementColors = ({
  designData,
  originalColor,
  color
}) => {
  const elementPreviews = {};

  if (!designData.restrictions.includes("backgroundColor")) {
    Object.entries(designData.pages).forEach(([pageId, page]) => {
      if (page.backgroundColor && isGradientValue(page.backgroundColor)) {
        if (isGradientValue(originalColor)) {
          // Replace entire gradient when it matches the original gradient
          if (
            JSON.stringify(page.backgroundColor) ===
            JSON.stringify(originalColor)
          ) {
            elementPreviews[pageId] = { backgroundColor: color };
          }
        } else if (!isGradientValue(color)) {
          // Only update stops when replacement is a hex (stops can't hold gradients)
          const gradient = page.backgroundColor;
          const hasMatch = gradient.stops.some(
            stop => sixDigitHexColor(stop.color) === originalColor
          );
          if (hasMatch) {
            elementPreviews[pageId] = {
              backgroundColor: {
                ...gradient,
                stops: gradient.stops.map(stop =>
                  sixDigitHexColor(stop.color) === originalColor
                    ? { ...stop, color }
                    : stop
                )
              }
            };
          }
        }
      } else if (
        !isGradientValue(originalColor) &&
        page.backgroundColor &&
        sixDigitHexColor(page.backgroundColor) === originalColor
      ) {
        elementPreviews[pageId] = { backgroundColor: color };
      }
    });
  }

  Object.values(designData.elements).forEach(element => {
    if ((element.restrictions || []).includes("color")) {
      return;
    }

    if (!hasMatchingColor(element, originalColor)) {
      return;
    }

    if (element.type === "table") {
      const mapCellsMeta = ({ textFields, ...other }) => ({
        ...other,
        textFields: textFields.map(args =>
          transformColor({ el: args, copyOriginal: true, originalColor, color })
        )
      });

      const mapCellsByRowType = (map, [rowTypeCode, { cellsMetadata }]) => ({
        ...map,
        [rowTypeCode]: {
          cellsMetadata: cellsMetadata.map(mapCellsMeta)
        }
      });

      elementPreviews[element.uniqueId] = {
        columnsMetadata: Object.entries(element.columnsMetadata).reduce(
          mapCellsByRowType,
          {}
        )
      };
    } else if (element.type === "table2") {
      const cellsClone = cloneDeep(element.cells);
      // iterate over table cells to update any matching colour to new colour
      // transformColor function returns object with only colour property updated
      // so we need to spread cell data with new colour update
      const updatedCells = {};
      Object.keys(cellsClone).forEach(cellId => {
        const cell = cellsClone[cellId];
        updatedCells[cellId] = {
          ...cell,
          ...transformColor({
            el: cell,
            originalColor,
            color
          })
        };
      });
      elementPreviews[element.uniqueId] = {
        cells: updatedCells
      };
    } else {
      elementPreviews[element.uniqueId] = transformColor({
        el: element,
        originalColor,
        color
      });
    }
  });

  return elementPreviews;
};

/**
 * @desc returns an object with element uniqueId as keys, containing attribute changes for matching colour
 * @param {string} attributeName - string representing the attribute update targetted for colour change
 * @param {string} color - string representing the HEX value for colour change
 * @param {boolean} isUpdateAll - boolean for trigger to check and update all elements with matching colour
 * @param {string} originalColor - string representing the HEX value for orignal elements attribute colour
 * @param {object} context - object from Editor state. Contains selected grid and table cells for targetted attribute update
 * @param {string} currentPageId - string representing current pageId in editor design
 * @param {object} designData - designData object
 * @param {Array} selectedItems - array of currently selected elements in the editor
 * @param {object} stateElementPreviews - object containing unprocessed preview updates for elements. Same structure as onColorChange
 * return object with uniqueId as key and attribute changes
 */
export const onColorChange = ({
  attributeName = "color",
  color: colorsProp,
  isUpdateAll = false,
  originalColor: originalColorProp,
  updateAllStopColor,
  fillOriginalColor,
  context,
  currentPageId,
  designData,
  selectedItems,
  stateElementPreviews,
  smartTextState
} = {}) => {
  const elementPreviews = {};

  if (!designData || isEmpty(designData) || !colorsProp) return elementPreviews;

  // Gradient objects should pass through without hex conversion
  const isGradient =
    typeof colorsProp === "object" &&
    colorsProp !== null &&
    "type" in colorsProp &&
    "stops" in colorsProp;
  const isOriginalGradient =
    typeof originalColorProp === "object" &&
    originalColorProp !== null &&
    "type" in originalColorProp &&
    "stops" in originalColorProp;
  const color = isGradient ? colorsProp : sixDigitHexColor(colorsProp);
  const originalColor = originalColorProp
    ? isOriginalGradient
      ? originalColorProp
      : sixDigitHexColor(originalColorProp)
    : null;

  const { cellIndex, rowIndex, textFieldIndex, selectedGridCellId } = context;

  if (isUpdateAll && !originalColor) {
    throw new Error("Invalid color change combo.");
  }

  if (isUpdateAll) {
    // When editing a gradient stop with Update All, the caller sends the full
    // gradient as `color` (so the selected item preview stays a gradient) and
    // the new stop hex as `updateAllStopColor`. Use the stop color for the
    // all-elements matching pass, then run the normal selected-items path for
    // the gradient so fillColors/backgroundColor/color are set correctly.
    const updateAllColor = updateAllStopColor
      ? sixDigitHexColor(updateAllStopColor)
      : color;

    const allPreviews = updateAllMatchingElementColors({
      designData,
      originalColor,
      color: updateAllColor
    });

    if (updateAllStopColor && isGradient) {
      // Re-run the normal (non-updateAll) path for the selected items so the
      // gradient is applied to the correct attribute (fillColors for vectors,
      // backgroundColor for pages, color for other elements).
      // Use fillOriginalColor (the element fill's original hex) instead of
      // originalColorProp (the stop hex) so the fill can be found in base data.
      const selectedPreviews = onColorChange({
        attributeName,
        color: colorsProp,
        isUpdateAll: false,
        originalColor: fillOriginalColor || originalColorProp,
        context,
        currentPageId,
        designData,
        selectedItems,
        stateElementPreviews,
        smartTextState
      });
      Object.assign(allPreviews, selectedPreviews);

      // When the original fill was a solid that was converted to gradient,
      // base data for other elements still has the solid (previews aren't
      // persisted). Apply the full gradient to those elements so they stay
      // in sync instead of reverting to the original solid.
      if (fillOriginalColor && !isGradientValue(fillOriginalColor)) {
        const solidToGradientPreviews = updateAllMatchingElementColors({
          designData,
          originalColor: sixDigitHexColor(fillOriginalColor),
          color: colorsProp
        });
        // Only fill in elements not already handled by the stop-level pass
        Object.keys(solidToGradientPreviews).forEach(id => {
          if (!allPreviews[id]) {
            allPreviews[id] = solidToGradientPreviews[id];
          }
        });
      }
    }

    return allPreviews;
  } else {
    // function checking if the color given matches the original color, not applicable for single color objects
    const shouldChange = ({ color: fillColor } = {}) => {
      if (!originalColor) return true;
      // When originalColor is a gradient, match the specific gradient fill by content
      if (isOriginalGradient) {
        return (
          isGradientValue(fillColor) &&
          JSON.stringify(fillColor) === JSON.stringify(originalColor)
        );
      }
      // Gradient fills were created from a different original color —
      // don't match them when originalColor is a hex
      if (isGradientValue(fillColor)) {
        return false;
      }
      return sixDigitHexColor(fillColor) === originalColor;
    };

    // Check if any selected items are caption elements
    const hasCaptionElement = selectedItems.some(({ itemId }) => {
      const element = designData.getElement(itemId);
      return element && element.type === EDITOR_ELEMENTS_MAP.CAPTION;
    });

    // Only set page background color if we're not working with caption elements
    if (attributeName === "backgroundColor" && !hasCaptionElement) {
      elementPreviews[currentPageId] = { backgroundColor: color };
    }

    selectedItems.forEach(({ itemId }) => {
      const element = designData.getElement(itemId);

      if (!element) {
        return;
      }

      // For table2 elements, allow cellBackgroundColor changes even when color (font color) is restricted
      if (
        element.restrictions.includes("color") &&
        !(element.type === "table2" && attributeName === "cellBackgroundColor")
      ) {
        return;
      }

      if (
        element.type === "table" &&
        cellIndex !== undefined &&
        rowIndex !== undefined &&
        textFieldIndex !== undefined
      ) {
        const { rowTypeCode } = element.rows[rowIndex];

        elementPreviews[element.uniqueId] = {
          columnsMetadata: immutableUpdate(element.columnsMetadata, {
            [rowTypeCode]: {
              cellsMetadata: {
                [cellIndex]: {
                  textFields: {
                    [textFieldIndex]: {
                      $merge: { color }
                    }
                  }
                }
              }
            }
          })
        };
      } else if (element.type === "table2") {
        const { selectedTable2CellIds } = context;
        let updatedCells = cloneDeep(element.cells);
        // reassign color for each selected cell in table
        selectedTable2CellIds.forEach(cellId => {
          const updatedCell = updatedCells[cellId];
          const updatedCellInnerHTML = removeColorFromRichText(updatedCell);
          if (attributeName === "cellBackgroundColor") {
            updatedCells = immutableUpdate(updatedCells, {
              [cellId]: {
                $merge: { backgroundColor: color }
              }
            });
          } else {
            updatedCells = immutableUpdate(updatedCells, {
              [cellId]: {
                $merge: {
                  color,
                  value: Boolean(updatedCellInnerHTML)
                    ? updatedCellInnerHTML
                    : updatedCell.value,
                  displayValue: Boolean(updatedCellInnerHTML)
                    ? updatedCellInnerHTML
                    : updatedCell.displayValue
                }
              }
            });
          }
        });
        elementPreviews[element.uniqueId] = {
          cells: updatedCells
        };
      } else if (
        element.type === EDITOR_ELEMENTS_MAP.CAPTION &&
        attributeName === "backgroundColor"
      ) {
        // Handle background color for caption elements
        // Update both backgroundColor and globalStyle.backgroundColor for consistency
        const currentGlobalStyle = element.globalStyle || {};
        elementPreviews[element.uniqueId] = {
          backgroundColor: color,
          globalStyle: {
            ...currentGlobalStyle,
            backgroundColor: color
          }
        };
      } else if (attributeName === "outline") {
        // shouldChange should not affect this update
        elementPreviews[element.uniqueId] = {
          outline: { ...element.outline, color }
        };
      } else if (element.fillColors && element.fillColors.some(shouldChange)) {
        // multiple colors checked so shouldChange is valid
        elementPreviews[element.uniqueId] = {
          fillColors: element.fillColors.map(fillColor =>
            shouldChange(fillColor) ? { ...fillColor, color } : { ...fillColor }
          )
        };
      } else if (attributeName === "shadow") {
        // shouldChange should not affect this update
        elementPreviews[element.uniqueId] = {
          shadow: { ...element.shadow, color }
        };
      } else if (attributeName === "textOutline") {
        elementPreviews[element.uniqueId] = {
          textOutline: { ...element.textOutline, color }
        };
      } else if (element.color) {
        // shouldChange should not affect this update
        elementPreviews[element.uniqueId] = { color };
        // handle color change when textbox has rich text changes
        if (element.type === "textbox" && !!element.value) {
          const updatedTextboxInnerHTML = removeColorFromRichText(element);
          if (updatedTextboxInnerHTML) {
            elementPreviews[element.uniqueId].value = updatedTextboxInnerHTML;
            elementPreviews[
              element.uniqueId
            ].displayValue = updateSmartTextPlaceholder(
              updatedTextboxInnerHTML,
              smartTextState
            );
          }
        }
      } else if (element.type === "grid" && selectedGridCellId) {
        const updatedGridImageInstructions = applyColorToImageInstruction({
          element,
          color,
          stateElementPreviews,
          selectedGridCellId
        });

        elementPreviews[element.uniqueId] = {
          imageInstructions: updatedGridImageInstructions
        };
      }
    });
  }

  return elementPreviews;
};
